import React, { CSSProperties, useCallback, useEffect, useMemo, useState } from "react";
import "./style.css";
import { Modal } from "../Default/Index";
import { RoundedCloseButton } from "../../Buttons/RoundedCloseButton/Index";
import { ButtonOnlyTitle } from "../../Buttons/ButtonOnlyTitle/Index";
import { useFilter } from "../../../hooks/useFilter";
import { useFilterContextV2 } from "../../../contexts/FilterContextV2";
import { firstFarmOptionFromV2Selection } from "../../../utils/filters/v2FarmSelection";

import axios from "../../../services/axios";
import { IFarms } from "../../../@types/API/IFarm";
import Constants from "../../../constants/Index";
import { getFarmsAsOptions } from "../../../utils/farms/farmsAsOptions";
import {
  IOptionProps,
  IOptionsProps,
} from "../../../@types/reactSelect/IOption";
import { IAreas } from "../../../@types/API/IArea";
import { getAreasAsOptions } from "../../../utils/farms/areasAsOptions";
import { sortBy } from "../../../utils/arrays/sortBy";
import { getMicroAreasAsOptions } from "../../../utils/farms/microAreasAsOptions";
import { IMicroAreas } from "../../../@types/API/IMicroArea";
import { ICoords } from "../../../@types/GoogleMaps/ICoord";
import { useTaskCreation } from "../../../hooks/useTaskCreation";
import { DrawMapElement } from "../../Tasks/DrawOnMap";
import { CreateTaskFilterStep } from "./taskFilters/CreateTaskFilterStep";
import { MIN_DETACHED_TASK_DETAILS_LENGTH } from "./taskFilters/types";
import { salterService } from "../../../services/salterService";

interface CreateTaskModalProps {
  visible: boolean;
  onClose: () => void;
  task: ITask;
  children: JSX.Element | "NO_PAGE";
  onGoBack: () => void;
}

const createTaskContentStyle = {
  padding: "50px",
  width: "650px",
  height: "fit-content",
  borderRadius: 20,
  display: "flex",
  flexDirection: "column",
} as CSSProperties;

const createTaskMobileContentStyle = {
  ...createTaskContentStyle,
  padding: "24px",
  width: "calc(100vw - 32px)",
  maxHeight: "calc(100vh - 32px)",
  borderRadius: 16,
  overflow: "hidden",
} as CSSProperties;

const needProductContentStyle = {
  padding: "20px",
  width: "600px",
  height: "fit-content",
  borderRadius: 20,
  display: "flex",
  flexDirection: "column",
} as CSSProperties;

const needProductMobileContentStyle = {
  ...needProductContentStyle,
  padding: "16px",
  width: "calc(100vw - 32px)",
  maxHeight: "calc(100vh - 32px)",
  borderRadius: 16,
  overflow: "hidden",
} as CSSProperties;

export const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  visible,
  onClose,
  task,
  children,
  onGoBack,
}) => {
  const {
    farmFilter,
    areaFilter,
    microAreaDefaultOption,
    microAreaFilter,
    setSystemManager,
    responseFarms,
  } = useFilter();

  const { selectedFarms } = useFilterContextV2();

  const [allAreas, setAllAreas] = useState<IAreas>();
  const [allMicroAreas, setAllMicroAreas] = useState<IMicroAreas>();

  const [selectedFarm, setSelectedFarm] = useState<any>();
  const [selectedArea, setSelectedArea] = useState<any>();
  const [selectedMicroArea, setSelectedMicroArea] = useState<any>();

  const [farmOptions, setFarmOptions] = useState<IOptionsProps>();
  const [areaOptions, setAreaOptions] = useState<IOptionsProps>();
  const [responsibleOptions, setResponsibleOptions] = useState<IOptionsProps>();

  const [currentFarm, setCurrentFarm] = useState<IOptionProps>();
  const [currentArea, setCurrentArea] = useState<IOptionProps>();
  const [currentMicroArea, setCurrentMicroArea] = useState<IOptionProps>();
  const [currentResponsible, setCurrentResponsible] = useState<IOptionProps>();

  const [allFieldsFilled, setAllFieldsFilled] = useState<boolean>(false);
  const [taskPage, setTaskPage] = useState<boolean>(false);

  const [areaResponsibles, setAreaResponsibles] = useState<IOptionsProps>([]);
  const [salterResponsibles, setSalterResponsibles] = useState<IOptionsProps>([]);
  const [taskDetails, setTaskDetails] = useState<string>("");

  const allResponsibles = useMemo(
    () =>
      [...areaResponsibles, ...salterResponsibles].filter(
        (option, index, options) =>
          options.findIndex(
            (candidate) => String(candidate.value) === String(option.value),
          ) === index,
      ),
    [areaResponsibles, salterResponsibles],
  );

  const {
    resetContext,
    setFarmId,
    setAreaId,
    setResponsibleId,
    setMicroAreaId,
    createTask,
    allowTaskCreation,
    taskCreatedSuccessfully,
    taskCreationError,
    setPayload,
    payload,
    setContextMicroArea,
    setAllAreas: setAreasContext,
    setAllMicroAreas: setMicroAreasContext,
    showDrawOnMapModal,
    currentCoords,
    setCurrentCoords,
    contextMicroArea,
    editFences,
  } = useTaskCreation();

  const [drawCoords, setDrawCoords] = useState<ICoords | undefined>(undefined);
  const [fitCoords, setFitCoords] = useState<ICoords>([]);
  const [needProduct, setNeedProduct] = useState(false);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.innerWidth <= 768;
  });

  const syncFieldsFromContext = useCallback(() => {
    const v2Farm = firstFarmOptionFromV2Selection(selectedFarms);
    if (v2Farm && responseFarms?.length) {
      const match = responseFarms.find(
        (f) => String(f.id) === String(v2Farm.value),
      );
      if (match) {
        setCurrentFarm({
          label: v2Farm.label,
          value: v2Farm.value,
          info: v2Farm.info,
        });
        setSelectedFarm(match);
        setFitCoords(match.map_coords || []);
      }
    } else if (farmFilter) {
      setCurrentFarm({
        label: `${farmFilter.name}`,
        value: farmFilter.id,
      });
      setFitCoords(farmFilter?.coords || []);
      const match = responseFarms?.find(
        (f) => String(f.id) === String(farmFilter.id),
      );
      if (match) setSelectedFarm(match);
    }

    if (areaFilter) {
      setCurrentArea({
        label: `${areaFilter.name}`,
        value: areaFilter.id,
      });
      setFitCoords(areaFilter?.coords || []);
    }
    if (microAreaDefaultOption) {
      setCurrentMicroArea(microAreaDefaultOption);
      setCurrentCoords(microAreaFilter.coords);
      setFitCoords(microAreaFilter?.coords || []);
      setDrawCoords(microAreaFilter?.coords);
    }
  }, [
    selectedFarms,
    responseFarms,
    farmFilter,
    areaFilter,
    microAreaDefaultOption,
    microAreaFilter,
    setCurrentCoords,
  ]);

  useEffect(() => {
    if (!visible) return;
    syncFieldsFromContext();
    setTaskPage(false);
    setNeedProduct(false);
  }, [visible, syncFieldsFromContext]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!responseFarms?.length) {
      setFarmOptions(undefined);
      return;
    }
    const options = getFarmsAsOptions(responseFarms as unknown as IFarms);
    setFarmOptions(options);
    if (farmFilter) {
      const match = responseFarms.find(
        (f) => String(f.id) === String(farmFilter.id),
      );
      setSelectedFarm(match);
    }
  }, [responseFarms, farmFilter]);

  useEffect(() => {
    if (currentFarm) {
      const farm = responseFarms?.find(
        (f) => String(f.id) === String(currentFarm.value),
      );
      setFitCoords(farm?.map_coords || []);
    }
  }, [currentFarm, responseFarms]);

  useEffect(() => {
    if (currentArea && allAreas) {
      const area = allAreas?.filter((area) => area.id == currentArea.value)[0];
      setFitCoords(area?.map_coords || []);
    }
  }, [currentArea, allAreas]);

  useEffect(() => {
    if (currentMicroArea && allMicroAreas) {
      const microArea = allMicroAreas?.filter(
        (microArea) => microArea.id == currentMicroArea.value,
      )[0];
      setFitCoords(microArea?.map_coords || []);
    }
  }, [currentMicroArea, allMicroAreas]);

  useEffect(() => {
    if (!taskCreatedSuccessfully) return;

    closeModal();
  }, [taskCreatedSuccessfully]);

  useEffect(() => {
    if (!taskCreationError) return;

    closeModal();
  }, [taskCreationError]);

  const handleFarmChange = (id: any) => {
    setCurrentArea(undefined);
    setCurrentResponsible(undefined);
    setCurrentMicroArea(undefined);
    setCurrentCoords([]);
    setDrawCoords(undefined);
    setAreaResponsibles([]);
    setSalterResponsibles([]);

    setCurrentFarm(farmOptions?.find((farm) => farm.value == id));
    setSelectedFarm(
      responseFarms?.find((f) => String(f.id) === String(id)) ?? undefined,
    );
  };

  useEffect(() => {
    if (!currentFarm) return;

    getAreas();
    getMicroAreas();
  }, [currentFarm]);

  useEffect(() => {
    if (!currentFarm || task.type !== "PRODUCT_INCOMING") {
      setSalterResponsibles([]);
      return;
    }

    let isCurrentRequest = true;
    salterService
      .listByFarm(currentFarm.value)
      .then((salters) => {
        if (!isCurrentRequest) return;
        setSalterResponsibles(
          salters.map((salter) => ({
            label: salter.user.name,
            value: salter.user.id,
          })),
        );
      })
      .catch(() => {
        if (isCurrentRequest) setSalterResponsibles([]);
      });

    return () => {
      isCurrentRequest = false;
    };
  }, [currentFarm, task.type]);

  const getAreas = () => {
    if (!currentFarm) return;

    axios
      .get(`/areas`, { params: { farm_id: currentFarm.value } })
      .then((response) => {
        if (response.status === Constants.STATUS_CODE_OK)
          initAreas(response.data);
      });
  };

  const initAreas = (data: any) => {
    const areaArray: IAreas = data;
    const options = getAreasAsOptions(areaArray).sort(sortBy("label"));
    const allResponsiblesList = areaArray
      .map((area) => ({
        label: area.manager_user?.name ?? "",
        value: area.manager_user?.id,
      }))
      .filter((opt) => opt.value !== undefined && opt.value !== null)
      .filter(
        (opt, index, self) =>
          self.findIndex((o) => o.value === opt.value) === index,
      ) as IOptionsProps;

    setAreaResponsibles(allResponsiblesList);
    setAllAreas(areaArray);
    setAreasContext(areaArray);
    setAreaOptions(options);
    if (areaFilter) {
      const area = areaArray.find((a) => a.id === areaFilter.id);
      const responsible = {
        label: area?.manager_user?.name ?? "",
        value: area?.manager_user?.id,
      };
      setCurrentResponsible(responsible);
      setResponsibleOptions([responsible]);
      setSelectedArea(area);
    }
  };

  const handleAreaChange = (id: any) => {
    setCurrentResponsible(undefined);
    setCurrentMicroArea(undefined);
    setCurrentCoords([]);
    setDrawCoords(undefined);

    setCurrentArea(areaOptions?.find((area) => area.value == id));
    setSelectedArea(allAreas?.find((area) => area.id === id) ?? undefined);
  };

  useEffect(() => {
    if (!currentArea) return;

    initResponsible();
  }, [currentArea]);

  const initResponsible = () => {
    if (!currentArea || !allAreas) return;

    const { name, id } = allAreas.find(
      (area) => area.id === currentArea.value,
    )?.manager_user!;

    if (!name || !id) return;
    setCurrentResponsible({
      label: name,
      value: id,
    });

    setResponsibleOptions([
      {
        label: name,
        value: id,
      },
    ]);
  };

  const getMicroAreas = () => {
    if (
      !currentFarm ||
      task.type === "DETACHED_TASK" ||
      task.type === "PRODUCT_INCOMING"
    )
      return;

    axios
      .get("micro-areas", {
        params: {
          farm_id: currentFarm.value,
        },
      })
      .then((response) => {
        if (response.status === Constants.STATUS_CODE_OK) {
          setAllMicroAreas(response.data);
          setMicroAreasContext(response.data);
        }
      });
  };

  function microAreaOptionsFiltered(): IOptionsProps {
    if (!currentArea || !allMicroAreas) return [];

    let label = `${selectedFarm?.initials || ""}${
      selectedArea ? `-${selectedArea.initials}-` : "-"
    }`;
    const filtered = allMicroAreas.filter(
      (el) => el.area_id === currentArea.value,
    );
    return getMicroAreasAsOptions(filtered, currentArea?.value, label);
  }

  const handleMicroAreaChange = (id: any) => {
    const microArea = allMicroAreas?.find((mA) => mA.id === id);
    setCurrentCoords(microArea?.map_coords || []);
    setDrawCoords(microArea?.map_coords);
    setCurrentMicroArea(
      microAreaOptionsFiltered().find((microArea) => microArea.value === id),
    );
    setSelectedMicroArea(microArea);
  };

  function getFarmLabel(): string {
    return farmFilter
      ? farmFilter.initials
      : (responseFarms?.find(
          (f) => String(f.id) === String(currentFarm?.value),
        )?.initials ?? "");
  }

  function getFarmOutlineCoords(): ICoords | undefined {
    if (farmFilter?.coords && farmFilter.coords.length > 0) {
      return farmFilter.coords;
    }
    const fromSelected = (
      selectedFarm as { map_coords?: ICoords } | undefined
    )?.map_coords;
    if (fromSelected && fromSelected.length > 0) {
      return fromSelected;
    }
    if (currentFarm && responseFarms?.length) {
      const f = responseFarms.find(
        (x) => String(x.id) === String(currentFarm.value),
      );
      if (f?.map_coords && f.map_coords.length > 0) {
        return f.map_coords;
      }
    }
    return undefined;
  }

  function getAreaLabel(): string {
    return areaFilter
      ? areaFilter.initials
      : (allAreas?.find((area) => area.id === currentArea?.value)?.initials ??
          "");
  }

  const createPayload = () => {
    if (task.type !== "DETACHED_TASK") {
      const reqPayload = {
        ...payload,
        changes: [
          {
            micro_area_id: currentMicroArea?.value,
            assigned_user_id: currentResponsible?.value,
          },
        ],
      };
      setPayload(reqPayload);
    } else {
      const reqPayload = {
        ...payload,
        changes: [
          {
            farm_id: currentFarm?.value,
            assigned_user_id: currentResponsible?.value,
            task_details: taskDetails,
          },
        ],
      };
      setPayload(reqPayload);
    }
  };

  useEffect(() => {
    setAllFieldsFilled(
      !(
        !currentFarm ||
        !currentResponsible ||
        !currentArea ||
        !currentMicroArea
      ),
    );

    if (task.body === "NO_PAGE") {
      createPayload();
    }

    if (task.type === "DETACHED_TASK") {
      return setAllFieldsFilled(
        !(!currentFarm || !currentResponsible || taskDetails.length < MIN_DETACHED_TASK_DETAILS_LENGTH),
      );
    }

    if (task.type === "PRODUCT_INCOMING") {
      axios.get(`/products`).then(({ data }) => {
        if (data.length == 0) setNeedProduct(true);
      });
      return setAllFieldsFilled(!(!currentFarm || !currentResponsible));
    }
  }, [
    currentArea,
    currentFarm,
    currentMicroArea,
    currentResponsible,
    taskDetails,
  ]);

  const startTaskCreation = () => {
    if (task.type != "PRODUCT_INCOMING") {
      if (!currentFarm || !currentArea) return;
      setTaskPage(true);
      setFarmId(currentFarm.value);
      setAreaId(currentArea.value);
      if (currentResponsible) setResponsibleId(currentResponsible.value ?? 0);
      if (currentMicroArea) {
        setMicroAreaId(currentMicroArea.value ?? 0);
        setContextMicroArea(
          allMicroAreas?.find(
            (microArea) => microArea.id === currentMicroArea.value,
          ) as Partial<IMicroArea>,
        );
      }
    } else {
      if (!currentFarm) return;
      setFarmId(currentFarm.value);
      if (currentResponsible) setResponsibleId(currentResponsible.value ?? 0);
      setTaskPage(true);
    }
  };

  const closeModal = () => {
    onClose();
    resetContext();
  };

  const standardFilterProps = {
    farmOptions: farmOptions ?? [],
    currentFarm,
    onFarmChange: handleFarmChange,
    farmLabel: getFarmLabel(),
    areaOptions: areaOptions ?? [],
    currentArea,
    onAreaChange: handleAreaChange,
    areaLabel: getAreaLabel(),
    responsibleOptions: responsibleOptions ?? [],
    currentResponsible,
    onResponsibleChange: (opt: IOptionProps | null) =>
      setCurrentResponsible(opt ?? undefined),
    microAreaOptions: microAreaOptionsFiltered(),
    currentMicroArea,
    onMicroAreaChange: handleMicroAreaChange,
    fitCoords,
    drawCoords,
    allMicroAreas,
    farmOutlineCoords: getFarmOutlineCoords(),
    onMapMicroAreaSelected: (e: { id?: number; area_id?: number } | undefined) => {
      if (currentArea?.value != e?.area_id)
        handleAreaChange(e?.area_id);
      else handleMicroAreaChange(e?.id);
    },
  };

  const detachedFilterProps = {
    farmOptions: farmOptions ?? [],
    currentFarm,
    onFarmChange: handleFarmChange,
    farmLabel: getFarmLabel(),
    allResponsibles: allResponsibles ?? [],
    currentResponsible,
    onResponsibleChange: (opt: IOptionProps | null) =>
      setCurrentResponsible(opt ?? undefined),
    taskDetails,
    onTaskDetailsChange: setTaskDetails,
  };

  const productIncomingFilterProps = {
    farmOptions: farmOptions ?? [],
    currentFarm,
    onFarmChange: handleFarmChange,
    farmLabel: getFarmLabel(),
    allResponsibles: allResponsibles ?? [],
    currentResponsible,
    onResponsibleChange: (opt: IOptionProps | null) =>
      setCurrentResponsible(opt ?? undefined),
  };

  return (
    <div style={{ zIndex: 901 }}>
      {needProduct ? (
        <Modal
          visible={visible}
          contentStyle={
            isMobile ? needProductMobileContentStyle : needProductContentStyle
          }
        >
          <div className="body-task">
            <div className="header-taskModal">
              <h2>Cadastre um produto!</h2>
              <RoundedCloseButton onClick={() => closeModal()} />
              <span>
                Você ainda não cadastrou um produto para criar essa tarefa, você
                pode criar através do gerenciamento do sistema.
              </span>
            </div>

            <footer className="buttons-container" style={{ width: "100%" }}>
              <ButtonOnlyTitle
                title="Cancelar"
                theme="light"
                onClick={() => onGoBack()}
              />
              <ButtonOnlyTitle
                title="Produtos"
                theme="info"
                onClick={() => {
                  setSystemManager("PRODUCTS");
                  onClose();
                }}
              />
            </footer>
          </div>
        </Modal>
      ) : (
        <Modal
          visible={visible}
          contentStyle={
            isMobile ? createTaskMobileContentStyle : createTaskContentStyle
          }
        >
          <header className="task-header">
            <div className="task-wrapper">
              {task.icon}
              <h2>{task.title}</h2>
            </div>
            <RoundedCloseButton onClick={() => closeModal()} />
          </header>
          {!taskPage ? (
            <>
              <main className="task-designation">
                <CreateTaskFilterStep
                  task={task}
                  standardProps={standardFilterProps}
                  detachedProps={detachedFilterProps}
                  productIncomingProps={productIncomingFilterProps}
                />
              </main>
              <footer className="buttons-container">
                <ButtonOnlyTitle
                  title="Voltar"
                  theme="light"
                  onClick={() => onGoBack()}
                />
                {task.body !== "NO_PAGE" ? (
                  <ButtonOnlyTitle
                    title="Continuar"
                    theme="info"
                    disabled={!allFieldsFilled}
                    onClick={() => startTaskCreation()}
                  />
                ) : (
                  <ButtonOnlyTitle
                    title="Criar Tarefa"
                    theme="info"
                    disabled={!allFieldsFilled}
                    onClick={() => createTask()}
                  />
                )}
              </footer>
            </>
          ) : (
            <>
              <main className="task-border">
                <div className="task-intrinsecs">{children}</div>
              </main>
              <footer className="buttons-container">
                <ButtonOnlyTitle
                  title="Voltar"
                  theme="light"
                  onClick={() => {
                    setTaskPage(false);
                    setCurrentCoords(contextMicroArea?.map_coords || []);
                  }}
                />
                <ButtonOnlyTitle
                  title="Criar tarefa"
                  theme="info"
                  disabled={!allowTaskCreation}
                  onClick={() => createTask()}
                />
              </footer>
            </>
          )}
        </Modal>
      )}

      {showDrawOnMapModal && allMicroAreas && (
        <DrawMapElement
          coords={currentCoords}
          setCoords={setCurrentCoords}
          editable={editFences}
          isMulti
        />
      )}
    </div>
  );
};
